/**
 * Hook for fetching AI-generated insights for dashboard sections
 */
import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';
import { API_URLS } from '@/constants/urls';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryKeyFactory';
import { useCompset } from '@/contexts/CompsetContext';


export function useAIInsight(section, data, enabled = true) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  const { activeCompset } = useCompset();

  const enableAiInsight = () => {
    setIsEnabled(true)
  };

  const queryClient = useQueryClient();

  const fetchInsight = useCallback(async (type = "regenerate") => {

    if (!enabled || !isEnabled || !section || !data) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(
        API_URLS.AI_INSIGHT,
        { section, data, type, compset_id: activeCompset?.compset_id },
      );

      if (response.status == 200) {
        // setInsight(response.data?.insight);
        setIsAIGenerated(response?.data.is_ai_generated || false);

        await Promise.all(
          [
            queryKeys.planRules(),
            queryKeys.allAiInsights(activeCompset?.compset_id)
          ]
            .map((queryKey) =>
              queryClient.invalidateQueries({ queryKey, type: "all" })
            )
        );

      }
    } catch (err) {
      console.error('AI Insight error:', err);
      setError(err.response?.data?.error || err.message);
      // Set fallback insight
      setInsight(getFallbackInsight(section));
      setIsAIGenerated(false);
    } finally {
      setLoading(false);
    }
  }, [section, data, enabled, isEnabled]);

  useEffect(() => {
    fetchInsight();
  }, [fetchInsight]);

  return { insight, loading, error, isAIGenerated, refetch: fetchInsight, isEnabled, enableAiInsight };
}

function getFallbackInsight(section) {
  const fallbacks = {
    competitive: "Analyzing your competitive position to identify pricing opportunities...",
    rate_parity: "Monitoring rate parity across OTA channels for revenue optimization...",
    market: "Tracking market trends to optimize your pricing strategy...",
    pricing: "Identifying dynamic pricing opportunities based on demand patterns...",
    advanced: "Analyzing OTA performance metrics for revenue optimization..."
  };
  return fallbacks[section] || "Analyzing data for insights...";
}

export default useAIInsight;
